import useIdentity from "@/features/ui/hooks/use-identity";
import { forwardRef, useCallback, useImperativeHandle, useMemo } from "react";
import { useRecoilValue } from "recoil";
import {
  OrderSheet,
  OrderSheetProduct,
} from "@/features/order-sheets/order-sheet.type";
import { Flex } from "@chakra-ui/react";
import AppButton from "@/features/line-sheet-sets/app-button";
import useDisclosureWithData from "@/features/order-sheet-sets/hooks/use-disclosure-with-data";
import { SubmitOrderSheetSetResponse } from "@/features/order-sheet-sets/hooks/use-submit-order-sheet-set";
import { OrderOrderSheetSetModalProps } from "@/features/order-sheet-sets/order-order-sheet-set-modal";
import {
  FlatProduct,
  inflatedTabsFamily,
} from "@/features/line-sheet-sets/helpers/sheet-state";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import AppToolTip, { AppToolTipProps } from "@/features/ui/app-tooltip";
import { ColumnApi, GridApi } from "ag-grid-community";
import { trackOrderButtonClick } from "@/shared/analytics/analytics";
import * as amplitude from "@amplitude/analytics-browser";
import OrderOrderSheetSetModalV2, {
  OrderOrderSheetSetModalV2Props,
} from "@/features/order-sheet-sets/order-order-sheet-set-modal-v2";
import { SubmitOrderSheetSetV2Response } from "@/features/order-sheet-sets/hooks/use-submit-order-sheet-set-v2";
import { CommissionType } from "@/features/line-sheet-sets/line-sheet-set.types";
import { CreateOrderSheetRevisionV2Response } from "@/features/line-sheet-sets/hooks/use-create-order-sheet-revision-v2";

interface OrderSheetSetOrderButtonProps {
  isOrderSheetEditing: boolean;
  isOrderSheetSetOrderable: boolean;
  isOnEditEnabledPage?: boolean;
  /*
   * 리오더가 되는 경우 oss는 제출 상태, os는 주문서 작성 상태가 된다,
   * 모든 os의 제출 여부로 ordered여부 판단한다.
   * */
  isAllOrderSheetsOrdered: boolean;
  orderSheetSetId: number;
  orderSheet?: { id: number } | undefined;
  orderedQuantity: number;
  revisionNumber?: number;
  // 주문 확인 모달이 쓰는 값들 — 페이지가 이미 조회해 둔 것을 그대로 넘겨 중복 조회를 없앤다.
  commission?: number | null;
  commissionType?: CommissionType | null;
  // 라인시트세트의 MOQ. 총 주문금액이 이보다 작으면 모달에 안내를 띄우고, 제출 하한보다 작으면 제출을 막는다.
  moq?: number | null;
  // 소량 주문 가능 세트인지(helpers/can-order-by-unit). true 면 최소주문금액으로 제출을 막지 않는다.
  canOrderByUnit?: boolean;
  currency?: string | null;
  hasRevision?: boolean;
  // 최소주문금액 미달 안내에서 패스픽으로 떠날 때 작성 중인 주문서를 저장했으면 불린다(페이지의 저장 후 처리).
  onAfterDraftSave?: (response: CreateOrderSheetRevisionV2Response) => void;
  onUpdatingInflatedProductCompleted: (isCompleted: boolean) => void;
  onOrderSheetOrderButtonClicked: (isClicked: boolean) => void;
  onSubmit: (response: SubmitOrderSheetSetV2Response) => void;
  gridRef: {
    current: {
      api: GridApi<FlatProduct>;
      columnApi: ColumnApi;
    } | null;
  };
}

export default forwardRef(function OrderSheetOrderButton(
  {
    isOrderSheetEditing,
    isOrderSheetSetOrderable,
    isOnEditEnabledPage,
    isAllOrderSheetsOrdered,
    orderSheetSetId,
    orderSheet,
    orderedQuantity,
    revisionNumber,
    commission,
    commissionType,
    moq,
    canOrderByUnit,
    currency,
    hasRevision,
    onAfterDraftSave,
    onUpdatingInflatedProductCompleted,
    onOrderSheetOrderButtonClicked,
    onSubmit,
    gridRef,
  }: OrderSheetSetOrderButtonProps,
  ref: React.Ref<{ handleOrderOrderSheet: () => void }>
) {
  const identity = useIdentity();
  const company = useMemo(() => {
    return identity?.company!!;
  }, [identity]);
  const { t, tTitle } = useI18nHelper();

  const sheetKey = useMemo(() => {
    return {
      orderSheetSetId: orderSheetSetId || -1,
      orderSheetId: orderSheet?.id || -1,
      revisionNumber: revisionNumber || -1,
    };
  }, [orderSheetSetId, orderSheet?.id, revisionNumber]);

  const inflatedTabs = useRecoilValue(inflatedTabsFamily(sheetKey));

  const isOrderSheetDirty = useMemo(() => {
    if (inflatedTabs) {
      return inflatedTabs.tabs.find((i) => i.isDirty) !== undefined;
    }
    return false;
  }, [inflatedTabs]);

  const orderSheetTabs = useMemo(() => {
    if (!inflatedTabs) {
      return [];
    }

    return inflatedTabs.tabs.map((tab) => {
      return {
        index: tab.index,
        products: tab.products.map((product): OrderSheetProduct => {
          return {
            id: product.id,
            priceAdjustedBy: product.priceAdjustedBy,
            supplyPrice: product.supplyPrice,
            firstOrderQuantityWithOptionList:
              product.firstOrderQuantityWithOptionList,
            prevOrderQuantityWithOptionList:
              product.prevOrderQuantityWithOptionList,
            latestOrderQuantityWithOptionList:
              product.latestOrderQuantityWithOptionList,
            orderQuantityWithOptionList: product.orderQuantityWithOptionList,
            newStockWithOptionList: product.newStockWithOptionList,
            confirmedPrice: product.confirmedPrice,
            confirmedPriceWithoutCondition:
              product.confirmedPriceWithoutCondition,
            confirmedPriceAdjustedBy: product.confirmedPriceAdjustedBy,
            confirmedQuantityWithOptionList:
              product.confirmedQuantityWithOptionList,
          };
        }),
      };
    });
  }, [inflatedTabs]);

  const {
    isOpen: isOrderOrderSheetSetModalOpen,
    onOpen: onOpenOrderOrderSheetSetModal,
    onClose: onCloseOrderOrderSheetSetModal,
    data: orderOrderSheetSetModalData,
    openWithData: openOrderOrderSheetSetModalWithData,
  } = useDisclosureWithData<OrderOrderSheetSetModalV2Props>();

  const tooltipProps = useMemo(():
    | Pick<AppToolTipProps, "level" | "label">
    | undefined => {
    if (isAllOrderSheetsOrdered) {
      return {
        level: "SUCCESS",
        label: t("common:order.disabled.already_ordered"),
      };
    } else if (!isOrderSheetSetOrderable) {
      return {
        level: "ERROR",
        label: t("common:order.disabled.not_state_of_being_ordered"),
      };
    } else if (isOnEditEnabledPage && !isOrderSheetDirty) {
      return {
        level: "ERROR",
        label: t("common:order.disabled.no_changes"),
      };
    } else if (orderedQuantity == 0) {
      return {
        level: "ERROR",
        label: t("common:order.disabled.no_ordered_quantity"),
      };
    }
    return undefined;
  }, [
    orderedQuantity,
    isOrderSheetSetOrderable,
    isAllOrderSheetsOrdered,
    t,
    isOnEditEnabledPage,
    isOrderSheetDirty,
  ]);

  const openOrderOrderSheetSetModal = useCallback(() => {
    if (orderSheetSetId && company.type === "BUYER") {
      // state updater 안이 아니라 여기서 보낸다 — updater 는 순수해야 하고,
      // Strict Mode 에서 두 번 호출되면 이벤트도 두 번 찍힌다.
      // 환경 게이트는 두지 않는다: GTM 컨테이너가 없으면 push 는 배열에 쌓이기만 하고,
      // 그래야 개발자가 로컬에서 window.dataLayer 로 페이로드를 확인할 수 있다.
      trackOrderButtonClick({
        identity,
        orderSheetSetId,
        orderSheetId: orderSheet?.id,
      });

      openOrderOrderSheetSetModalWithData((prev) => {
        return {
          orderSheetSetId: orderSheetSetId,
          // 주문서 내에서는 해당 주문서만 주문 가능하도록 함
          orderSheetIds: orderSheet?.id ? [orderSheet.id] : [],
          orderSheetTabs,
          onSubmit,
          commission,
          commissionType,
          moq,
          canOrderByUnit,
          currency,
          hasRevision,
        };
      });
    }
  }, [
    orderSheetSetId,
    company.type,
    openOrderOrderSheetSetModalWithData,
    orderSheet?.id,
    orderSheetTabs,
    onSubmit,
    identity,
    commission,
    commissionType,
    moq,
    canOrderByUnit,
    currency,
    hasRevision,
  ]);

  const handleOrderClick = useCallback(async () => {
    /* 편집 중이던 상태에서 클릭했다면 강제로 편집 모드를 종료하면서 변경된 데이터를 업데이트해두지만,
     * 편집 중이지 않은 상태였다면 이미 업데이트가 완료된 상태임.
     * 따라서 편집 중이지 않은 상태였다면 데이터 업데이트가 완료되었다고 판단하기 위해 상태값을 사용함.
     */
    if (!isOrderSheetEditing) {
      onUpdatingInflatedProductCompleted(true);
    }
    /* Editing 상태가 종료될 때 Save 버튼을 클릭해서 강제로 종료된 것인지, 사용자가 직접 종료한 것인지 구분하기 위해 사용
     * Save 버튼 클릭에 의한 종료라면 오더시트세트를 저장해야하기 때문
     */
    onOrderSheetOrderButtonClicked(true);
    /* 편집 중이던 상태에서 Save 버튼을 클릭하면 강제로 편집 모드를 종료함. 편집 모드가 종료되지 않으면 저장 시 편집 중이던 데이터가 저장되지 않을 수 있음.
     * AG Grid에서 Editing 종료 이벤트가 발생하면, OrderSheetTab에서 inflatedProduct를 업데이트하게 됨.
     * inflatedProduct가 업데이트된 이후에 저장을 진행해야 편집 중이던 데이터까지 저장할 수 있음.
     */
    gridRef?.current?.api.stopEditing();
    amplitude.track("Order Button Clicked");
  }, [
    onUpdatingInflatedProductCompleted,
    onOrderSheetOrderButtonClicked,
    gridRef,
    isOrderSheetEditing,
  ]);

  const handleOrderOrderSheet = useCallback(async () => {
    if (orderSheetSetId && company?.type === "BUYER") {
      openOrderOrderSheetSetModal();
      onOrderSheetOrderButtonClicked(false);
    }
  }, [
    orderSheetSetId,
    company.type,
    onOrderSheetOrderButtonClicked,
    openOrderOrderSheetSetModal,
  ]);

  /* 상위 컴포넌트에서 미리 만들어서 넘겨준 ref에 본 컴포넌트의 함수를 할당함
   * 이렇게 하면 상위 컴포넌트에서 ref.current.handleOrderOrderSheet()로 호출할 수 있음
   * Order의 로직은 본 컴포넌트에서 정의하고,
   * 상위 컴포넌트에서 호출(하단 stopEditing 처리 후 order로직을 실행할 수 있도록)하기 위해 이와 같은 구조를 사용함
   */
  useImperativeHandle(ref, () => ({
    handleOrderOrderSheet,
  }));

  return (
    <>
      <AppToolTip
        level={tooltipProps?.level}
        hasArrow={true}
        label={tooltipProps?.label}
        isDisabled={tooltipProps === undefined}
        modifiers={[
          {
            name: "preventOverflow",
            options: {
              padding: 8,
            },
          },
        ]}
      >
        <Flex>
          <AppButton
            width={"80px"}
            variant={"primary"}
            isDisabled={tooltipProps !== undefined}
            onClick={handleOrderClick}
          >
            {tTitle("order.submit")}
          </AppButton>
        </Flex>
      </AppToolTip>
      {isOrderOrderSheetSetModalOpen && orderOrderSheetSetModalData && (
        <OrderOrderSheetSetModalV2
          {...orderOrderSheetSetModalData}
          isOpen={isOrderOrderSheetSetModalOpen}
          isOrderSheetDirty={isOrderSheetDirty}
          // 제출한 오더를 수정하는 화면(isOnEditEnabledPage)에는 저장이 없다 — 작성 중인 주문서만 저장 가능
          isDraftSavable={!isOnEditEnabledPage}
          onAfterDraftSave={onAfterDraftSave}
          onClose={onCloseOrderOrderSheetSetModal}
          revisionNumber={revisionNumber}
        />
      )}
    </>
  );
});
